import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { PaymentProvider } from './contexts/PaymentContext';
import { AppointmentProvider } from './contexts/AppointmentContext';
import { MessagingProvider } from './contexts/MessagingContext';
import ProtectedRoute from './components/ProtectedRoute';
import DashboardLayout from './components/DashboardLayout';
import ScrollToTop from './components/ScrollToTop';
import { Home, Heart, Baby, Calendar, Users, FileText, TrendingUp, MapPin, Home as HomeIcon } from 'lucide-react';

// Public Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import SignupPage from './pages/SignupPage';

// Patient Pages
import PatientDashboard from './pages/patient/PatientDashboard';
import HealthRecords from './pages/patient/HealthRecords';
import ChildHealth from './pages/patient/ChildHealth';
import Appointments from './pages/patient/Appointments';
import BookAppointment from './pages/patient/BookAppointment';
import ServiceCheckout from './pages/patient/ServiceCheckout';
import Consultation from './pages/patient/Consultation';
import HomeVisitRequests from './pages/patient/HomeVisitRequests';

// Doctor Pages
import DoctorDashboard from './pages/doctor/DoctorDashboard';
import Patients from './pages/doctor/Patients';
import PatientRecord from './pages/doctor/PatientRecord';
import DoctorHealthTracking from './pages/doctor/HealthTracking';
import DoctorAppointments from './pages/doctor/Appointments';
import DoctorConsultation from './pages/doctor/Consultation';

// Nurse Pages
import NurseDashboard from './pages/nurse/NurseDashboard';
import PatientRecords from './pages/nurse/PatientRecords';
import NursePatients from './pages/nurse/Patients';
import PatientRecordDetail from './pages/nurse/PatientRecordDetail';
import HomeVisits from './pages/nurse/HomeVisits';
import NurseAppointments from './pages/nurse/Appointments';

function App() {
  // Patient sidebar items - Streamlined flow
  const patientSidebarItems = [
    { path: '/patient/dashboard', label: 'Dashboard', icon: <Home className="w-5 h-5" /> },
    { path: '/patient/appointments', label: 'Care & Appointments', icon: <Calendar className="w-5 h-5" /> },
    { path: '/patient/home-visit-requests', label: 'Home Visits', icon: <HomeIcon className="w-5 h-5" /> },
    { path: '/patient/health', label: 'Health Records', icon: <Heart className="w-5 h-5" /> },
    { path: '/patient/child-health', label: 'Child Health', icon: <Baby className="w-5 h-5" /> },
  ];

  // Doctor sidebar items
  const doctorSidebarItems = [
    { path: '/doctor/dashboard', label: 'Dashboard', icon: <Home className="w-5 h-5" /> },
    { path: '/doctor/appointments', label: 'Appointments', icon: <Calendar className="w-5 h-5" /> },
    { path: '/doctor/patients', label: 'Patients', icon: <Users className="w-5 h-5" /> },
    { path: '/doctor/health-tracking', label: 'Health Tracking', icon: <TrendingUp className="w-5 h-5" /> },
  ];

  // Nurse sidebar items
  const nurseSidebarItems = [
    { path: '/nurse/dashboard', label: 'Dashboard', icon: <Home className="w-5 h-5" /> },
    { path: '/nurse/appointments', label: 'Appointments', icon: <Calendar className="w-5 h-5" /> },
    { path: '/nurse/patients', label: 'Patients', icon: <Users className="w-5 h-5" /> },
    { path: '/nurse/patient-records', label: 'Patient Records', icon: <FileText className="w-5 h-5" /> },
    { path: '/nurse/home-visits', label: 'Home Visits', icon: <MapPin className="w-5 h-5" /> },
  ];

  return (
    <AuthProvider>
      <LanguageProvider>
        <PaymentProvider>
          <AppointmentProvider>
            <MessagingProvider>
              <Router>
                <ScrollToTop />
                <Routes>
                  {/* Public Routes */}
                  <Route path="/" element={<LandingPage />} />
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/signup" element={<SignupPage />} />

              {/* Patient Routes */}
              <Route
                path="/patient/dashboard"
                element={
                  <ProtectedRoute allowedRoles={['patient']}>
                    <DashboardLayout items={patientSidebarItems} role="patient">
                      <PatientDashboard />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/patient/health"
                element={
                  <ProtectedRoute allowedRoles={['patient']}>
                    <DashboardLayout items={patientSidebarItems} role="patient">
                      <HealthRecords />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/patient/child-health"
                element={
                  <ProtectedRoute allowedRoles={['patient']}>
                    <DashboardLayout items={patientSidebarItems} role="patient">
                      <ChildHealth />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/patient/pay-per-service/:serviceId"
                element={
                  <ProtectedRoute allowedRoles={['patient']}>
                    <DashboardLayout items={patientSidebarItems} role="patient">
                      <ServiceCheckout />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/patient/appointments"
                element={
                  <ProtectedRoute allowedRoles={['patient']}>
                    <DashboardLayout items={patientSidebarItems} role="patient">
                      <Appointments />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/patient/book-appointment"
                element={
                  <ProtectedRoute allowedRoles={['patient']}>
                    <DashboardLayout items={patientSidebarItems} role="patient">
                      <BookAppointment />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/patient/home-visit-requests"
                element={
                  <ProtectedRoute allowedRoles={['patient']}>
                    <DashboardLayout items={patientSidebarItems} role="patient">
                      <HomeVisitRequests />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/patient/consultation/:appointmentId"
                element={
                  <ProtectedRoute allowedRoles={['patient']}>
                    <Consultation />
                  </ProtectedRoute>
                }
              />

              {/* Doctor Routes */}
              <Route
                path="/doctor/dashboard"
                element={
                  <ProtectedRoute allowedRoles={['doctor']}>
                    <DashboardLayout items={doctorSidebarItems} role="doctor">
                      <DoctorDashboard />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/doctor/appointments"
                element={
                  <ProtectedRoute allowedRoles={['doctor']}>
                    <DashboardLayout items={doctorSidebarItems} role="doctor">
                      <DoctorAppointments />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/doctor/patients"
                element={
                  <ProtectedRoute allowedRoles={['doctor']}>
                    <DashboardLayout items={doctorSidebarItems} role="doctor">
                      <Patients />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/doctor/patient-record/:patientId"
                element={
                  <ProtectedRoute allowedRoles={['doctor']}>
                    <DashboardLayout items={doctorSidebarItems} role="doctor">
                      <PatientRecord />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/doctor/health-tracking"
                element={
                  <ProtectedRoute allowedRoles={['doctor']}>
                    <DashboardLayout items={doctorSidebarItems} role="doctor">
                      <DoctorHealthTracking />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/doctor/consultation/:appointmentId"
                element={
                  <ProtectedRoute allowedRoles={['doctor']}>
                    <DoctorConsultation />
                  </ProtectedRoute>
                }
              />

              {/* Nurse Routes */}
              <Route
                path="/nurse/dashboard"
                element={
                  <ProtectedRoute allowedRoles={['nurse']}>
                    <DashboardLayout items={nurseSidebarItems} role="nurse">
                      <NurseDashboard />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nurse/appointments"
                element={
                  <ProtectedRoute allowedRoles={['nurse']}>
                    <DashboardLayout items={nurseSidebarItems} role="nurse">
                      <NurseAppointments />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nurse/patients"
                element={
                  <ProtectedRoute allowedRoles={['nurse']}>
                    <DashboardLayout items={nurseSidebarItems} role="nurse">
                      <NursePatients />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nurse/patient-record/:patientId"
                element={
                  <ProtectedRoute allowedRoles={['nurse']}>
                    <DashboardLayout items={nurseSidebarItems} role="nurse">
                      <PatientRecordDetail />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nurse/patient-records"
                element={
                  <ProtectedRoute allowedRoles={['nurse']}>
                    <DashboardLayout items={nurseSidebarItems} role="nurse">
                      <PatientRecords />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nurse/home-visits"
                element={
                  <ProtectedRoute allowedRoles={['nurse']}>
                    <DashboardLayout items={nurseSidebarItems} role="nurse">
                      <HomeVisits />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/nurse/health-tracking"
                element={
                  <ProtectedRoute allowedRoles={['nurse']}>
                    <DashboardLayout items={nurseSidebarItems} role="nurse">
                      <DoctorHealthTracking />
                    </DashboardLayout>
                  </ProtectedRoute>
                }
              />

              {/* Fallback Route */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Router>
            </MessagingProvider>
          </AppointmentProvider>
        </PaymentProvider>
      </LanguageProvider>
    </AuthProvider>
  );
}

export default App;
